import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, DUCK_LOGO, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, ShieldCheck, ChevronRight, PlayCircle, Play, Sparkles,
  LayoutDashboard, Rocket, LineChart, GitBranch, Lock, Star, HelpCircle, ExternalLink, Users,
  TrendingUp, Zap, Award, Clock, DollarSign, Landmark, Gamepad2,
} from "lucide-react";
import {
  TrustBar, DashboardShowcase, FeaturesGrid, FundingCenterBlock,
  CreditMonitoringBlock, ProgressTimelineBlock, WhyChooseBlock,
  ComparisonBlock, SecurityBlock, StatsBlock, TestimonialsBlock, FAQBlock, CTABanner,
  CreditDataTrustBlock, EcosystemBlock, TrustBadgesRow, SecureCheckoutStrip,
  ClientApprovalsBlock, MeetTeamBlock,
} from "./HomeSections";
import SlotStatBar from "@/components/app/SlotStatBar";
import DuckyAvatar from "@/components/app/DuckyAvatar";
import QuackManModal from "@/components/app/QuackManModal";

export default function Home() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const signupRef = useRef(null);
  const dashboardRef = useRef(null);
  const tabsRef = useRef(null);
  const [tab, setTab] = useState("overview");
  const [quackOpen, setQuackOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const scrollToSignup = () => signupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToDashboard = () => dashboardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goToTab = (t) => {
    setTab(t);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };

  const passwordScore = useMemo(() => {
    const p = form.password || "";
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }, [form.password]);

  const passwordLabel = ["", "Weak", "Fair", "Good", "Strong"][passwordScore];

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, password: form.password,
      });
      if (data.token) localStorage.setItem("cq_token", data.token);
      setUser(data);
      toast.success(`Welcome, ${data.first_name || form.first_name}! Taking you to your dashboard…`);
      // Small delay so the user sees the toast before route change
      setTimeout(() => nav("/portal"), 400);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const goToDashboard = () => nav(user?.role === "client" ? "/portal" : "/admin");

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* =============== NAV =============== */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-brand">
            <img src={DUCK_LOGO} alt="Credit Quack" className="h-10 w-10 object-contain cq-waddle" />
            <div className="leading-tight">
              <p className="font-display font-black text-slate-900 text-lg">Credit Quack</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Client Portal</p>
            </div>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => goToTab("overview")} className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="nav-features">Features</button>
            <button onClick={() => goToTab("funding")} className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="nav-funding">Funding</button>
            <button onClick={() => goToTab("process")} className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="nav-how">How it works</button>
            <button onClick={() => goToTab("faq")} className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="nav-faq">FAQ</button>
            <a href="/docs" className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="nav-docs">Help</a>
            <a href="https://www.CreditQuack.com" target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-amber-600 hover:text-amber-700" data-testid="nav-official-site">
              CreditQuack.com <ExternalLink size={13} />
            </a>
            {user && user !== false ? (
              <button onClick={goToDashboard} className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-white h-10 px-4 rounded-xl font-bold text-sm" data-testid="home-goto-dashboard">
                Go to dashboard <ChevronRight size={16} className="ml-1" />
              </button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-2" data-testid="home-signin-link">Sign in</Link>
                <button onClick={scrollToSignup} className="inline-flex items-center bg-slate-900 hover:bg-slate-800 text-white h-10 px-4 rounded-xl font-bold text-sm" data-testid="home-signup-cta">
                  Get started
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* =============== HERO =============== */}
      <section className="relative overflow-hidden">
        <div className="cq-aurora absolute inset-0" />
        <div className="cq-dotgrid absolute inset-0 opacity-[0.35]" />
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-amber-200/40 blur-3xl cq-float" />
        <div className="absolute -bottom-40 -left-40 h-[560px] w-[560px] rounded-full bg-sky-200/40 blur-3xl cq-float-delay" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-8 lg:pb-14">
          <div className="max-w-4xl mx-auto text-center cq-fade-up">
            <div className="cq-hero-badge inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold text-slate-700">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400/70 animate-ping" />
                <span className="relative inline-block h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Trusted platform for credit repair &amp; funding
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.98] text-slate-900 mt-6">
              The Most Transparent<br />
              <span className="cq-text-gradient-gold">Credit Repair &amp; Funding</span> Platform
            </h1>
            <p className="text-slate-600 text-lg sm:text-xl mt-6 max-w-3xl mx-auto">
              Track your credit repair in real time, securely upload documents, communicate directly with your specialist, monitor your funding readiness, and prepare for approvals — all from one powerful dashboard.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button onClick={scrollToSignup} className="cq-btn-primary inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-14 px-7 rounded-2xl font-black text-base" data-testid="hero-signup-primary">
                Create My Free Client Portal <ArrowRight size={18} />
              </button>
              <button onClick={scrollToDashboard} className="cq-btn-secondary inline-flex items-center gap-2 bg-white border border-slate-200 h-14 px-6 rounded-2xl font-bold text-slate-800" data-testid="hero-watch-demo">
                <PlayCircle size={20} className="text-amber-500" /> Watch demo
              </button>
            </div>
            <SlotStatBar
              testid="hero-slot-bar"
              intervalMs={5200}
              items={[
                { icon: Star,        text: "4.9★ from 4,200+ clients" },
                { icon: DollarSign,  text: "$50M+ in funding facilitated" },
                { icon: TrendingUp,  text: "+94 pt average score improvement" },
                { icon: Award,       text: "Trusted by 90+ credit specialists" },
                { icon: Clock,       text: "Most disputes cycle in 30–90 days" },
                { icon: Landmark,    text: "Preps clients for 21+ lender programs" },
                { icon: Zap,         text: "Real-time status — no waiting on email" },
                { icon: ShieldCheck, text: "SOC 2-aligned infrastructure" },
              ]}
            />
          </div>

          {/* Hero preview card */}
          <div ref={dashboardRef} className="mt-14 lg:mt-20 max-w-6xl mx-auto cq-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="rounded-[28px] p-1.5 bg-gradient-to-br from-amber-200 via-slate-200 to-sky-200 shadow-2xl">
              <div className="rounded-[22px] bg-slate-950 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 border-b border-white/5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                  <div className="ml-3 rounded-md bg-white/5 px-3 py-1 text-[10px] text-slate-300 font-mono">clientquack.com/portal</div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 p-6 bg-slate-950 text-white">
                  <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Live progress</p>
                    <p className="font-display text-2xl font-black mt-1">Step 6 of 10</p>
                    <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full w-3/5 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full cq-progress-fill" />
                    </div>
                  </div>
                  <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">Funding readiness</p>
                    <p className="font-display text-2xl font-black mt-1">742 · Strong</p>
                    <p className="text-xs text-emerald-400 mt-1 font-bold">▲ +38 this month</p>
                  </div>
                  <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300">Estimated funding</p>
                    <p className="font-display text-2xl font-black mt-1">$85,000</p>
                    <p className="text-xs text-slate-400 mt-1">across 7 matched products</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =============== ENTER QUACK CITY — point-and-click game mode =============== */}
          <div className="mt-16 lg:mt-24 max-w-5xl mx-auto cq-fade-up" data-testid="quack-city-section">
            <div className="relative overflow-hidden rounded-3xl bg-[#070a1c] text-white px-6 py-10 sm:px-12 sm:py-14 grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-center shadow-2xl">
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 20%, rgba(76,29,149,0.45), transparent 55%), radial-gradient(ellipse at 90% 90%, rgba(34,211,238,0.18), transparent 50%)" }} />
              <div className="relative text-left">
                <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-300">Quack City · An investment in the next generation</p>
                <h2 className="font-display text-4xl sm:text-5xl font-black mt-2 leading-[1.05]">
                  A free game that teaches kids <span className="text-pink-400">money</span>, <span className="text-cyan-300">music</span> and <span className="text-amber-300">design</span>
                </h2>
                <p className="text-slate-300 mt-4 text-base sm:text-lg max-w-xl">
                  U.S. adults now score 47% on financial literacy — Gen Z scores 38%. Quack City fixes that where kids already are: a living pixel city with a real economy, a professional music studio, a clothing designer that ships real merch, and credit lessons in every building. Free for every kid. Open to investors, brands and media partners.
                </p>
                <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-bold">
                  {[["30+", "buildings"], ["6", "world cities"], ["40", "collectibles"], ["3", "pro studios"], ["$0", "cost to a kid"]].map(([n, l]) => (
                    <span key={l} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-slate-200"><span className="text-cyan-300">{n}</span> {l}</span>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/quackcity"
                    className="inline-flex items-center gap-3 rounded-full font-black text-slate-950 h-14 px-8 text-base sm:text-lg bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 hover:from-amber-200 hover:to-orange-300 shadow-[0_18px_40px_-10px_rgba(251,191,36,0.6)] transition transform hover:-translate-y-0.5"
                    data-testid="quackcity-investor-link"
                  >
                    See the mission, stats & how to join
                  </Link>
                  <Link
                    to="/city"
                    className="inline-flex items-center gap-2 rounded-full font-bold text-cyan-200 h-14 px-6 text-sm border border-cyan-400/40 hover:bg-cyan-400/10 transition"
                    data-testid="enter-quack-city"
                  >
                    <Gamepad2 size={16} /> Play free
                  </Link>
                </div>
              </div>
              <div className="relative h-44 sm:h-56 rounded-2xl overflow-hidden border border-cyan-400/30 bg-gradient-to-b from-[#1a1740] to-[#2b2a5e]">
                <div className="absolute bottom-0 inset-x-0 h-10 bg-[#2b2f3a]" />
                <div className="absolute bottom-10 left-6 w-20 h-28 bg-slate-200 rounded-t-sm"><div className="absolute -top-3 -left-1 w-22 h-3 bg-teal-700" style={{ width: "5.5rem" }} /><div className="absolute top-6 left-4 w-3 h-4 bg-amber-200" /><div className="absolute top-6 right-4 w-3 h-4 bg-amber-200" /><div className="absolute bottom-0 left-7 w-6 h-10 bg-slate-900" /></div>
                <div className="absolute bottom-10 left-32 w-28 h-36 bg-stone-300 rounded-t-sm"><div className="absolute top-6 left-3 w-2 h-28 bg-stone-100" /><div className="absolute top-6 left-9 w-2 h-28 bg-stone-100" /><div className="absolute top-6 right-9 w-2 h-28 bg-stone-100" /><div className="absolute top-6 right-3 w-2 h-28 bg-stone-100" /><div className="absolute -top-2 inset-x-0 h-2 bg-amber-500" /></div>
                <div className="absolute bottom-10 right-8 w-24 h-24 bg-[#0f0a1e] border border-pink-400/50"><p className="absolute top-3 inset-x-0 text-center text-[8px] font-black text-pink-400 tracking-widest">ARCADE</p></div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 h-24 w-auto"><img src="/game/duck_front.webp" alt="Ducky" className="h-full w-auto" draggable={false} /></div>
                <div className="absolute top-3 right-3 text-[9px] font-black tracking-widest text-cyan-200/80">▸ click to walk</div>
              </div>
            </div>
          </div>

          {/* =============== TRY QUACKMAN — arcade easter-egg mini-game =============== */}
          <div className="mt-16 lg:mt-24 max-w-4xl mx-auto text-center cq-fade-up" data-testid="try-quackman-section">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Take a break</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-2 leading-[1.05]">
              Try <span className="bg-gradient-to-r from-amber-500 via-amber-600 to-orange-500 bg-clip-text text-transparent">QuackMan</span>
            </h2>
            <p className="text-slate-600 mt-3 text-base sm:text-lg max-w-2xl mx-auto">
              Chomp the dots, dodge the credit villains, and clear the maze. Because credit repair should feel like leveling up — not homework.
            </p>
            <button
              type="button"
              onClick={() => setQuackOpen(true)}
              className="cq-quack-launcher group mt-8 inline-flex items-center gap-3 rounded-full font-black text-slate-950 h-14 px-8 text-base sm:text-lg bg-gradient-to-r from-amber-400 via-amber-500 to-orange-400 hover:from-amber-300 hover:via-amber-400 hover:to-orange-300 shadow-[0_18px_40px_-10px_rgba(251,191,36,0.6)] transition transform hover:-translate-y-0.5 hover:shadow-[0_22px_44px_-8px_rgba(251,191,36,0.75)] active:translate-y-0"
              data-testid="quackman-launch"
              aria-haspopup="dialog"
              aria-expanded={quackOpen}
            >
              <Play size={18} className="fill-slate-950" />
              <span>Play QuackMan</span>
              <span className="ml-1 hidden sm:inline text-slate-950/60 text-xs font-bold">Arcade mini-game</span>
            </button>
          </div>
        </div>
      </section>

      {/* QuackMan modal — mounts (and starts the game loop) only when open */}
      <QuackManModal open={quackOpen} onClose={() => setQuackOpen(false)} />

      {/* =============== TABBED EXPLORE =============== */}
      <section ref={tabsRef} className="relative py-14 lg:py-20 bg-gradient-to-b from-white via-slate-50 to-white scroll-mt-24" data-testid="explore-tabs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Explore the platform</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-3 leading-[1.05]">
              Everything you need — organized, not overwhelming
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Tap a tab below to dive into exactly what matters to you. Same platform. Cleaner exploration.
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="home-tabs">
            <div className="overflow-x-auto -mx-4 px-4 pb-2">
              <TabsList className="inline-flex w-max min-w-full sm:min-w-0 sm:flex sm:flex-wrap sm:justify-center gap-1.5 bg-white border border-slate-200 rounded-2xl p-1.5 shadow-sm h-auto">
                <TabTrig value="overview" icon={LayoutDashboard} label="Overview" />
                <TabTrig value="team" icon={Users} label="Our Team" />
                <TabTrig value="funding" icon={Rocket} label="Funding" />
                <TabTrig value="monitoring" icon={LineChart} label="Credit Monitoring" />
                <TabTrig value="process" icon={GitBranch} label="How It Works" />
                <TabTrig value="security" icon={Lock} label="Security & Stats" />
                <TabTrig value="reviews" icon={Star} label="Reviews" />
                <TabTrig value="faq" icon={HelpCircle} label="FAQ" />
              </TabsList>
            </div>

            <TabsContent value="overview" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-overview">
              <TrustBar />
              <DashboardShowcase scrollToSignup={scrollToSignup} />
              <TrustBadgesRow />
              <FeaturesGrid />
            </TabsContent>

            <TabsContent value="team" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-team">
              <MeetTeamBlock />
            </TabsContent>

            <TabsContent value="funding" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-funding">
              <FundingCenterBlock />
              <EcosystemBlock />
              <ClientApprovalsBlock />
            </TabsContent>

            <TabsContent value="monitoring" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-monitoring">
              <CreditDataTrustBlock />
              <CreditMonitoringBlock />
            </TabsContent>

            <TabsContent value="process" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-process">
              <ProgressTimelineBlock />
              <WhyChooseBlock />
              <ComparisonBlock />
            </TabsContent>

            <TabsContent value="security" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-security">
              <SecurityBlock />
              <StatsBlock />
            </TabsContent>

            <TabsContent value="reviews" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-reviews">
              <TestimonialsBlock />
            </TabsContent>

            <TabsContent value="faq" className="mt-10 focus-visible:outline-none" data-testid="tab-panel-faq">
              <FAQBlock />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* =============== MID CTA =============== */}
      <CTABanner
        scrollToSignup={scrollToSignup}
        headline="Start Your Credit Journey Today"
        sub="Full portal access is free — sign up in 30 seconds and see your first status update within the day."
        cta="Access My Secure Dashboard"
        testid="cta-mid"
      />

      {/* =============== SIGNUP FORM =============== */}
      <section id="signup" ref={signupRef} className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Free · 30 seconds</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-3 leading-[1.05]">
              Prepare for Better Funding — Start Here
            </h2>
          </div>

          <Card className="p-6 sm:p-10 rounded-3xl border-slate-100 shadow-xl">
            <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FloatField id="first_name" label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="home-first-name" />
                  <FloatField id="last_name" label="Last name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="home-last-name" />
                </div>
                <FloatField id="email" type="email" label="Email address" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="home-email" />
                <FloatField id="phone" label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="home-phone" />
                <div>
                  <FloatField id="password" type="password" label="Create a password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="home-password" />
                  {form.password && (
                    <div className="mt-2" data-testid="password-strength">
                      <div className="grid grid-cols-4 gap-1">
                        {[1,2,3,4].map((k) => (
                          <div key={k} className={`h-1.5 rounded-full cq-strength-${passwordScore >= k ? passwordScore : 0}`} />
                        ))}
                      </div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-1">
                        Strength: <span className={
                          passwordScore >= 4 ? "text-emerald-600" :
                          passwordScore === 3 ? "text-emerald-500" :
                          passwordScore === 2 ? "text-amber-600" : "text-red-500"
                        }>{passwordLabel || "…"}</span>
                      </p>
                    </div>
                  )}
                </div>
                <FloatField id="confirm" type="password" label="Confirm password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} required testid="home-confirm" />
                {err && <p className="text-sm text-red-600" data-testid="home-signup-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white h-14 rounded-2xl text-base font-black shadow-lg" data-testid="home-signup-submit">
                  {busy ? "Creating account…" : (<>Create My Free Client Portal <ArrowRight size={18} className="ml-2" /></>)}
                </button>
                <p className="text-xs text-slate-500 text-center pt-1">
                  Already have an account? <Link to="/login" className="text-amber-600 font-bold">Sign in</Link>. By signing up you agree to Credit Quack&rsquo;s Terms &amp; Privacy Policy.
                </p>
              </form>
          </Card>

          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-slate-500 font-semibold">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-600" /> Bank-level encryption</span>
            <span className="inline-flex items-center gap-1"><Sparkles size={14} className="text-amber-500" /> AI-powered guidance</span>
          </div>
        </div>

        {/* Secure Checkout / Guarantee Strip */}
        <SecureCheckoutStrip />
      </section>

      {/* =============== BOTTOM CTA =============== */}
      <CTABanner
        scrollToSignup={scrollToSignup}
        headline="Track Your Progress in Real Time"
        sub="Your first status update usually posts within 24 hours of signup."
        cta="Get Started Free"
        testid="cta-bottom"
      />

      {/* =============== FOOTER =============== */}
      <footer className="bg-slate-950 text-slate-300 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-2">
                <img src={DUCK_LOGO} alt="Credit Quack" className="h-10 w-10 object-contain" />
                <div className="leading-tight">
                  <p className="font-display font-black text-white text-lg">Credit Quack</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Client Portal</p>
                </div>
              </Link>
              <p className="text-sm text-slate-400 mt-4 max-w-sm">The most transparent credit repair &amp; funding platform. Built for clients who want to see every step, not just the outcome.</p>
              <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-bold text-slate-300" data-testid="footer-trust-strip">
                <span className="inline-flex items-center gap-1"><ShieldCheck size={13} className="text-emerald-400" /> Secure Client Portal</span>
                <span className="text-slate-600">&bull;</span>
                <span>Bank-Level Encryption</span>
                <span className="text-slate-600">&bull;</span>
                <span>Trusted by Thousands</span>
              </div>
            </div>

            <FooterCol title="Company" links={[
              { label: "About", to: "#" },
              { label: "Contact Us", to: "#contact" },
              { label: "Careers", to: "#" },
            ]} />
            <FooterCol title="Resources" links={[
              { label: "Funding Center", to: "#funding-center" },
              { label: "Credit Education", to: "#" },
              { label: "FAQs", to: "#faq" },
              { label: "How it works", to: "#how-it-works" },
            ]} />
            <FooterCol title="Legal" links={[
              { label: "Privacy Policy", to: "/privacy" },
              { label: "Terms of Service", to: "/terms" },
              { label: "Contact Us", to: "#contact" },
              { label: "Accessibility", to: "/accessibility" },
              { label: "Client Login", to: "/login" },
            ]} />
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 space-y-4 text-xs text-slate-500">
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 text-[11px] leading-relaxed">
              <p className="font-bold text-slate-300 uppercase tracking-widest text-[10px] mb-2">Compliance disclaimer</p>
              <p>
                Credit Quack is not a credit bureau, lender, bank, or financial institution. Credit Quack provides credit education, organization tools, funding-readiness insights, and client support. Approval for any credit or funding product is not guaranteed and depends on lender criteria, credit profile, income, documentation, and underwriting. All third-party logos and screenshots shown on this site (including but not limited to Experian, Equifax, TransUnion, American Express, Discover, Visa, Mastercard, Chase, Bank of America, Capital One, Navy Federal Credit Union, PNC, TD Bank, SunTrust, PayPal, McAfee, Trustpilot, IdentityIQ, Dun &amp; Bradstreet, the Better Business Bureau (BBB), and the U.S. Small Business Administration) are property of their respective owners and are used solely for educational and informational reference. No official partnership, sponsorship, accreditation, or endorsement is claimed unless specifically stated in writing.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
              <p data-testid="footer-copyright">© 2026 Credit Quack. All Rights Reserved.</p>
              <p>Results vary. Compatibility with 3rd-party monitoring tools depends on your provider.</p>
            </div>
          </div>

          {/* Hidden easter-egg — tiny duck opens the secret access modal */}
          <div className="flex justify-center mt-8">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("cq:open-secret"))}
              aria-label="Ducky secret access"
              title=""
              className="group h-8 w-8 rounded-full flex items-center justify-center opacity-30 hover:opacity-100 hover:scale-110 focus:opacity-100 focus:outline-none transition-all duration-300"
              data-testid="footer-duck-egg"
            >
              <span className="select-none group-hover:animate-bounce" aria-hidden="true">
                <DuckyAvatar size={28} bare />
              </span>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TabTrig({ value, icon: Icon, label }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={`home-tab-${value}`}
      className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-md transition whitespace-nowrap"
    >
      <Icon size={16} strokeWidth={2.4} />
      <span>{label}</span>
    </TabsTrigger>
  );
}

function FloatField({ id, label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="cq-float-field">
      <input id={id} name={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder=" " data-testid={testid} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="font-display font-black text-white text-sm uppercase tracking-widest">{title}</p>
      <ul className="mt-4 space-y-2 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            {l.to.startsWith("#") ? (
              <a href={l.to} className="text-slate-400 hover:text-amber-300 transition">{l.label}</a>
            ) : (
              <Link to={l.to} className="text-slate-400 hover:text-amber-300 transition">{l.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
